import { SHOT_OUTCOME_LABELS } from '@golf/common';
import type { ShotOutcome } from '@golf/common';
import { ArrowLeft, Check, ChevronRight, ClipboardCheck, RotateCcw, Save, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import { formatDuration } from '../../lib/duration.ts';
import { ClubLabel } from '../../components/ClubLabel.tsx';
import { modeLabel } from '../../lib/modes.ts';
import type { RunSummary as Summary } from '../../lib/planning.ts';
import { getRunSummary, setRunNotes, startWorkout } from '../../lib/planning.ts';
import { useSettings } from '../../lib/settings.tsx';
import { formatDistance } from '../../lib/units.ts';
import { ComparisonBars } from './ComparisonBars.tsx';
import type { ComparisonRow } from './ComparisonBars.tsx';

/* Le bilan d'une séance terminée.
 *
 * ── Pourquoi comparer avec la fois précédente DU MÊME PLAN ──────────────────
 *
 * C'est la seule comparaison honnête dont dispose l'application. Deux jours de
 * practice pris au hasard n'ont ni les mêmes exercices ni le même ordre ; deux
 * exécutions d'une même séance, si. Écrire un plan et le refaire, c'est
 * précisément ce qui rend un écart interprétable — et c'est pour ça que la
 * séance existe.
 *
 * Quand il n'y a pas de fois précédente, la comparaison est ABSENTE. Un
 * « −64 points » contre un néant, le jour de la première séance, serait un
 * mensonge décourageant.
 *
 * ── Ce que l'écran met en avant, dans l'ordre ───────────────────────────────
 *
 *   1. le taux global et son écart — la réponse à « ça a donné quoi ? » ;
 *   2. le détail par exercice — où l'on a gagné, où l'on a perdu ;
 *   3. le détail par club — ce qu'on emporte au practice suivant ;
 *   4. les fautes, de la plus coûteuse à la moins.
 *
 * Puis un bouton pour refaire la séance : c'est ce qu'on veut faire d'un plan
 * qui a marché, et le chemin serait sinon un retour en arrière de trois écrans.
 */

const dateLong = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

const num = (v: string | null): number | null => (v === null ? null : Number(v));

/** La coche d'une condition d'examen. La FORME distingue les deux états autant
 *  que la couleur : en plein soleil, un vert et un rouge de même clarté
 *  finissent par se ressembler. */
function Marque({ ok }: { ok: boolean }) {
    return (
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full"
            style={{
                background: ok ? 'var(--score-gagne)' : 'var(--brique)',
                color: ok ? 'var(--fairway-deep)' : 'var(--brique-encre)',
            }}>
            {ok ? <Check size={14} strokeWidth={3} /> : <X size={14} strokeWidth={3} />}
        </span>
    );
}

export function RunSummary() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { unit } = useSettings();
    const [data, setData] = useState<Summary | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    /** Brouillon de note. Séparé de `data` : on tape, on n'a pas encore
     *  enregistré, et un rechargement du bilan ne doit pas effacer la frappe. */
    const [noteDraft, setNoteDraft] = useState('');
    const [noteSaved, setNoteSaved] = useState(false);

    const load = useCallback(async () => {
        if (!id) return;
        try {
            const s = await getRunSummary(id);
            setData(s);
            setNoteDraft(s.run.notes ?? '');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        }
    }, [id]);

    useEffect(() => { void load(); }, [load]);

    const saveNotes = async (): Promise<void> => {
        if (!id) return;
        setBusy(true); setError(null);
        try {
            await setRunNotes(id, noteDraft);
            setNoteSaved(true);
            // Rechargement plutôt que mise à jour locale : le serveur décide
            // ce qui est stocké — il rogne les blancs et transforme une chaîne
            // vide en absence de note. Recopier le brouillon ferait diverger
            // l'écran de la base dès le premier espace en trop.
            await load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
        } finally { setBusy(false); }
    };

    const refaire = async (): Promise<void> => {
        if (!data?.run.workoutId) return;
        setBusy(true);
        try {
            await startWorkout(data.run.workoutId);
            void navigate('/entrainement/seance-en-cours');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Démarrage impossible');
            setBusy(false);
        }
    };

    if (error) {
        return (
            <div className="space-y-4 py-2">
                <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>
                <Link to="/entrainement" className="btn btn-ghost btn-block">Retour à l'entraînement</Link>
            </div>
        );
    }
    if (!data) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    const {
        run, totals, previous, previousNotes, plannedItems, items, courseSteps,
        clubs, faults, pointsTarget, pointsMax, completionNote,
        points: pointsFaits, previousPoints,
    } = data;

    /* ── Un examen ne se lit pas en pourcentage ──────────────────────────────
     *
     * Le bilan ouvrait sur un taux de réussite. Quatre-vingts pour cent, c'est
     * une bonne séance ; ce n'est pas une réponse à « est-ce que j'ai eu mon
     * test ? ». La fédération compte des POINTS — les balles centrées — et
     * demande deux choses, pas une : un total minimum ET l'étape de parcours.
     *
     * Les deux conditions s'affichent donc séparément, chacune avec son
     * verdict. Un seul chiffre global les aurait fondues, et un passage à
     * vingt-six points sans parcours validé se serait annoncé comme réussi. */
    const examen = pointsTarget !== null;
    /* Les points viennent du SERVEUR. Les recompter ici, en additionnant les
     * balles centrées, ignorait deux règles que lui seul tient : le barème
     * borne chaque atelier, et une seule séance compte par rang. */
    const pointsOk = pointsTarget !== null && pointsFaits >= pointsTarget;
    /* Un parcours non JOUÉ ne vaut pas un parcours raté, mais il ne vaut pas
     * non plus une validation : dans les deux cas la condition n'est pas
     * remplie, et c'est ce que la ligne doit dire. */
    const parcoursOk = courseSteps.length === 0 || courseSteps.every((c) => c.passed === true);
    const reussi = pointsOk && parcoursOk;
    const nombre = (v: number): string =>
        v.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
    const ecartPoints = previousPoints !== null ? pointsFaits - previousPoints : null;

    /* ── Ce qu'il faut aller travailler ──────────────────────────────────────
     *
     * La séance sert à s'entraîner, pas à constater. Le bilan doit donc
     * désigner les ateliers qui ont coûté le plus de points — ce sont eux qui
     * séparent du seuil, et ce sont eux qu'on ira refaire.
     *
     * Le tri se fait sur les POINTS PERDUS, pas sur le taux : deux points
     * manqués sur cinq pèsent autant que deux sur dix dans le total, et c'est
     * le total qui décide. À égalité, l'atelier le moins bien réussi passe
     * devant — c'est celui où il reste le plus à gagner. */
    const faiblesses = examen ? [
        ...courseSteps
            .filter((c) => c.passed !== true)
            .map((c) => ({
                cle: `p${c.position}`,
                nom: c.name ?? 'Parcours',
                exerciseId: null as string | null,
                points: null as number | null,
                max: null as number | null,
                manque: Number.POSITIVE_INFINITY,
                detail: c.passed === null ? 'jamais joué'
                    : `Par +${Number(c.avgOverPar).toFixed(1)}, il faut +${Number(c.maxAvgOverPar)} au plus`,
            })),
        ...items
            .filter((it) => it.pointsMax != null && it.center < it.pointsMax)
            .map((it) => ({
                cle: String(it.position),
                nom: it.exerciseName ?? modeLabel(it.mode ?? ''),
                exerciseId: it.exerciseId,
                points: it.center,
                max: it.pointsMax,
                manque: (it.pointsMax ?? 0) - it.center,
                detail: null as string | null,
            }))
            .sort((a, b) => b.manque - a.manque
                || (a.points ?? 0) / (a.max ?? 1) - (b.points ?? 0) / (b.max ?? 1))
            .slice(0, 3),
    ] : [];
    /** Ce qui sépare encore du seuil, quand il n'est pas atteint. */
    const manquePourValider = pointsTarget !== null && !pointsOk
        ? pointsTarget - pointsFaits : null;
    /* Arrêté en cours de route : moins d'étapes faites que prévues au plan.
     *
     * Les étapes de PARCOURS comptent, elles aussi. Elles n'ouvrent aucune
     * séance d'entraînement, donc `totals.exercises` les ignore — et une
     * séance menée à son terme s'annonçait « arrêtée après 7 exercices sur
     * 8 ». Un parcours COMMENCÉ suffit : il a bien eu lieu, réussi ou non. */
    const etapesFaites = totals.exercises
        + data.courseSteps.filter((c) => c.passed !== null).length;
    const ecourte = plannedItems > 0 && etapesFaites < plannedItems;
    const rate = totals.successRate;
    const delta = rate !== null && previous?.successRate != null
        ? rate - previous.successRate
        : null;

    // Une seule ligne par exercice, dans l'ordre du plan. Le libellé porte le
    // rang : deux étapes peuvent avoir le même nom, et c'est le rang qui les
    // distingue — comme sur le déroulé.
    const itemRows: ComparisonRow[] = items.map((it) => ({
        key: String(it.position),
        label: `${it.position}. ${it.exerciseName ?? modeLabel(it.mode ?? '')}`,
        current: num(it.successRate) ?? 0,
        previous: num(it.previousRate),
    }));

    const clubRows: ComparisonRow[] = clubs.map((c) => ({
        key: c.clubId,
        // Même raison que dans la synthèse d'exercice : le type seul dans le
        // graphique, le détail dans la liste.
        label: c.label,
        current: num(c.successRate) ?? 0,
        previous: num(c.previousRate),
    }));

    const totalFautes = faults.reduce((s, f) => s + f.n, 0);

    return (
        <div className="space-y-5 py-2">
            <header>
                <Link to="/entrainement" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Entraînement
                </Link>
                {/* « Terminée » ou « arrêtée » : arrêter en gardant est une
                    sortie légitime — seau vide, pluie — et le bilan ne doit ni
                    la cacher ni la présenter comme un échec. Il dit simplement
                    où l'on s'est arrêté. */}
                <p className={`text-xs font-semibold uppercase tracking-wide ${ecourte ? 'opacity-70' : 'text-primary'}`}>
                    {ecourte
                        ? `Arrêté après ${etapesFaites} étape${etapesFaites > 1 ? 's' : ''} sur ${plannedItems}`
                        : 'Entraînement terminé'}
                </p>
                <h1 className="text-2xl font-bold tracking-tight">{run.name}</h1>
                <p className="mt-1 text-sm opacity-60">{dateLong(run.startedAt)}</p>
            </header>

            {/* Le résultat, en grand. Sur un examen c'est le TOTAL EN POINTS,
                pas le taux : la fédération compte des points, et un
                pourcentage ne dit pas si l'épreuve est passée. */}
            {examen ? (
                <section className="overflow-hidden rounded-box bg-base-200 text-center">
                    <div className="p-5">
                        {/* Le total suit SA condition, pas le verdict d'ensemble :
                            écrit en rouge parce que le parcours a manqué, un total
                            de vingt-six points — au-dessus du seuil — se lisait
                            comme un total insuffisant. */}
                        <p className="font-score text-6xl font-bold leading-none tabular-nums"
                            style={{ color: pointsOk ? 'var(--fairway-deep)' : 'var(--brique-encre)' }}>
                            {pointsFaits}
                            <span className="text-3xl opacity-45"> / {pointsMax}</span>
                        </p>
                        <p className="mt-1 text-sm opacity-60">
                            points marqués aux ateliers
                        </p>

                        {/* Les DEUX conditions, chacune avec son verdict. Fondues
                            en un seul chiffre, un passage à vingt-six points sans
                            parcours validé se serait annoncé comme réussi. */}
                        <ul className="mt-4 space-y-1.5 text-left">
                            <li className="flex items-center gap-2 rounded-field bg-base-100 px-3 py-2">
                                <Marque ok={pointsOk} />
                                <span className="min-w-0 flex-1 text-sm">
                                    <span className="font-semibold tabular-nums">
                                        {nombre(pointsTarget)} points
                                    </span> au minimum
                                </span>
                                <span className="font-score shrink-0 text-sm tabular-nums opacity-60">
                                    {pointsFaits}
                                </span>
                            </li>
                            {courseSteps.map((c) => (
                                <li key={c.position}
                                    className="flex items-center gap-2 rounded-field bg-base-100 px-3 py-2">
                                    <Marque ok={c.passed === true} />
                                    <span className="min-w-0 flex-1 text-sm">
                                        <span className="font-semibold">{c.name ?? 'Parcours'}</span>
                                        {c.maxAvgOverPar != null
                                            && <span className="opacity-60"> · Par +{Number(c.maxAvgOverPar)} au plus</span>}
                                    </span>
                                    <span className="shrink-0 text-sm tabular-nums opacity-60">
                                        {c.passed === null ? 'non joué'
                                            : c.avgOverPar != null ? `+${Number(c.avgOverPar).toFixed(1)}` : ''}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Le verdict, en pied de bloc : c'est la dernière ligne qu'on
                        lit, et la seule qui réponde à la question posée. */}
                    <p className="px-5 py-3 text-lg font-bold"
                        style={{
                            background: reussi ? 'var(--score-gagne)' : 'var(--brique)',
                            color: reussi ? 'var(--fairway-deep)' : 'var(--brique-encre)',
                        }}>
                        {reussi ? 'Partie pratique validée' : 'Partie pratique non validée'}
                    </p>

                    <div className="flex flex-wrap justify-center gap-2 p-3 text-sm">
                        {ecartPoints !== null && (
                            <span className="badge badge-lg tabular-nums">
                                {ecartPoints > 0 ? '+' : ''}{ecartPoints} pt{Math.abs(ecartPoints) > 1 ? 's' : ''}
                                {' '}vs. la fois d’avant
                            </span>
                        )}
                        <span className="badge badge-lg">{totals.shots} balles</span>
                        <span className="badge badge-lg">{formatDuration(totals.seconds * 1000)}</span>
                    </div>
                </section>
            ) : (
                /* Le chiffre, en grand, et son écart juste dessous. C'est la
                   réponse à la question qu'on se pose en rangeant ses clubs. */
                <section className="rounded-box bg-base-200 p-5 text-center">
                    <p className="text-6xl font-bold leading-none text-primary tabular-nums">
                        {rate === null ? '—' : `${rate.toFixed(0)}%`}
                    </p>
                    <p className="mt-1 text-sm opacity-60">de balles réussies</p>

                    {delta !== null && previous ? (
                        <p className="mt-3 text-sm">
                            <span className={`font-bold ${delta > 0 ? 'text-success' : delta < 0 ? 'text-error' : ''}`}>
                                {delta > 0 ? '+' : ''}{delta.toFixed(1)} point{Math.abs(delta) >= 2 ? 's' : ''}
                            </span>
                            {' '}par rapport à votre séance précédente
                            {' '}({previous.successRate?.toFixed(0)} %).
                        </p>
                    ) : (
                        // Premier passage : pas de comparaison possible, et le dire
                        // franchement vaut mieux qu'un vide qu'on prendrait pour un
                        // bogue.
                        <p className="mt-3 text-sm opacity-60">
                            Première fois que vous faites cette séance. Ce chiffre devient
                            votre référence pour les prochaines.
                        </p>
                    )}

                    <div className="mt-4 flex justify-center gap-2 text-sm">
                        <span className="badge badge-lg">{totals.shots} balles</span>
                        <span className="badge badge-lg">{totals.exercises} exercices</span>
                        <span className="badge badge-lg">{formatDuration(totals.seconds * 1000)}</span>
                    </div>
                </section>
            )}

            {/* À travailler, juste sous le verdict : c'est la suite logique de
                « non validée », et la raison d'être de la séance. Chaque ligne
                ouvre l'atelier, sans avoir à le rechercher dans la liste. */}
            {faiblesses.length > 0 && (
                <section className="rounded-box bg-base-200 p-4">
                    <h2 className="text-sm font-semibold">
                        {reussi ? 'À travailler pour consolider' : 'À travailler pour valider'}
                    </h2>
                    <p className="mb-3 mt-1 text-xs opacity-60">
                        {manquePourValider !== null
                            ? `Il manque ${nombre(manquePourValider)} point${manquePourValider > 1 ? 's' : ''} : voici où ils se sont perdus.`
                            : 'Les ateliers qui ont coûté le plus de points.'}
                    </p>
                    <ul className="space-y-2">
                        {faiblesses.map((f) => {
                            const contenu = (
                                <>
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-semibold">{f.nom}</span>
                                        <span className="block text-xs opacity-60">
                                            {f.detail ?? `${f.manque} point${f.manque > 1 ? 's' : ''} perdu${f.manque > 1 ? 's' : ''}`}
                                        </span>
                                    </span>
                                    {f.max != null && (
                                        <span className="font-score shrink-0 text-sm tabular-nums opacity-70">
                                            {f.points} / {f.max}
                                        </span>
                                    )}
                                </>
                            );
                            return (
                                <li key={f.cle}>
                                    {f.exerciseId ? (
                                        <Link to={`/entrainement/exercices?exercice=${f.exerciseId}`}
                                            className="flex items-center gap-3 rounded-field bg-base-100 p-3
                                                       tap-target transition-colors hover:bg-base-300">
                                            {contenu}
                                            <ChevronRight size={18} className="shrink-0 opacity-40" aria-hidden="true" />
                                        </Link>
                                    ) : (
                                        <div className="flex items-center gap-3 rounded-field bg-base-100 p-3">
                                            {contenu}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            {/* Les notes, juste sous le chiffre.
                Les statistiques disent ce qui s'est passé, pas ce qu'on a
                senti — et c'est le ressenti qu'on aura oublié dans huit jours.
                Placées avant les détails parce que c'est maintenant qu'on s'en
                souvient, pas après avoir lu trois graphiques. */}
            <section className="rounded-box bg-base-200 p-4">
                <h2 className="text-sm font-semibold">Vos notes</h2>
                <p className="mt-1 text-xs opacity-60">
                    Ce que vous avez ressenti, ce qui a gêné, la météo. Modifiable
                    plus tard : trois mots sur le parking, le reste ce soir.
                </p>
                <textarea
                    rows={4}
                    maxLength={4000}
                    className="textarea textarea-bordered mt-2 w-full text-base"
                    placeholder="Bas du swing enfin stable au fer 7. Épaule droite raide à partir du troisième tour. Vent de face, distances à relativiser."
                    value={noteDraft}
                    onChange={(e) => { setNoteDraft(e.target.value); setNoteSaved(false); }}
                />
                <div className="mt-2 flex items-center gap-3">
                    <button type="button"
                        disabled={busy || noteDraft === (run.notes ?? '')}
                        onClick={() => void saveNotes()}
                        className="btn btn-primary btn-sm gap-2 tap-target">
                        <Save size={16} /> Enregistrer
                    </button>
                    {/* Un accusé explicite : sans lui, on ne sait pas si le
                        texte est parti, et on réécrit tout par précaution. */}
                    {noteSaved && (
                        <span className="flex items-center gap-1 text-xs text-success">
                            <Check size={14} /> Enregistré
                        </span>
                    )}
                </div>

                {previousNotes && (
                    <div className="mt-4 border-t border-base-300 pt-3">
                        <p className="text-xs font-semibold uppercase tracking-wide opacity-50">
                            La fois précédente
                        </p>
                        {/* Souvent l'explication d'un écart : « épaule
                            douloureuse » ou « vent de face » valent mieux
                            qu'une conclusion tirée d'un taux qu'on aurait pris
                            pour un niveau. */}
                        <p className="mt-1 whitespace-pre-wrap text-sm opacity-80">{previousNotes}</p>
                    </div>
                )}
            </section>

            {/* Les trois encarts suivants sont ceux d'une séance de practice, et
                ils n'ont rien à dire d'un examen.
                — « Exercice par exercice » compare des POURCENTAGES quand la
                  note est en points, et répète ce que la liste dit mieux ;
                — « Par club » classe des clubs alors que l'épreuve impose l'outil
                  à chaque atelier : le classement ne mesure que le programme ;
                — « Ce qui vous a coûté » agrège les fautes de sept ateliers qui
                  n'ont ni la même distance ni le même geste — un top au bunker et
                  un slice au driver comptent pour la même chose. */}
            {!examen && itemRows.length > 0 && (
                <section className="rounded-box bg-base-200 p-4">
                    <h2 className="text-sm font-semibold">Exercice par exercice</h2>
                    <p className="mb-3 mt-1 text-xs opacity-60">
                        Dans l'ordre du plan.
                        {previous
                            ? ' La silhouette montre le même exercice la fois précédente.'
                            : ' Vous verrez la fois précédente en regard dès la deuxième exécution.'}
                    </p>
                    <ComparisonBars
                        rows={itemRows} max={100} unit="%"
                        currentLabel="Cette séance" previousLabel="Séance précédente"
                    />
                </section>
            )}

            {!examen && clubRows.length > 0 && (
                <section className="rounded-box bg-base-200 p-4">
                    <h2 className="text-sm font-semibold">Par club</h2>
                    {/* Ordre du sac, comme partout ailleurs : on vise une
                        position connue au lieu de relire chaque libellé. */}
                    <p className="mb-3 mt-1 text-xs opacity-60">
                        Dans l'ordre de votre sac, du plus long au putter.
                    </p>
                    <ComparisonBars
                        rows={clubRows} max={100} unit="%"
                        currentLabel="Cette séance" previousLabel="Séance précédente"
                    />
                    <ul className="mt-3 space-y-1 text-sm">
                        {clubs.map((c) => (
                            <li key={c.clubId} className="flex items-baseline justify-between gap-3">
                                <span className="min-w-0 truncate">
                                    <ClubLabel club={c} className="text-sm" />
                                </span>
                                <span className="shrink-0 tabular-nums opacity-60">
                                    {c.center}/{c.shots}
                                    {c.distanceAvgM != null
                                        && ` · ${formatDistance(Number(c.distanceAvgM), unit)}`}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {!examen && faults.length > 0 && (
                <section className="rounded-box bg-base-200 p-4">
                    <h2 className="text-sm font-semibold">Ce qui vous a coûté</h2>
                    <p className="mb-3 mt-1 text-xs opacity-60">
                        {totalFautes} balle{totalFautes > 1 ? 's' : ''} manquée{totalFautes > 1 ? 's' : ''},
                        de la faute la plus fréquente à la plus rare.
                    </p>
                    <ul className="space-y-2">
                        {faults.map((f) => {
                            const part = totalFautes > 0 ? (f.n * 100) / totalFautes : 0;
                            return (
                                <li key={f.outcome}>
                                    <div className="flex items-baseline justify-between gap-3 text-sm">
                                        <span className="font-semibold">
                                            {SHOT_OUTCOME_LABELS[f.outcome as ShotOutcome] ?? f.outcome}
                                        </span>
                                        <span className="tabular-nums opacity-60">×{f.n}</span>
                                    </div>
                                    {/* Barre fine, une seule teinte : c'est la
                                        LONGUEUR qui porte l'information, la
                                        couleur n'a rien à ré-encoder. */}
                                    <div className="mt-1 h-2 w-full rounded-full bg-base-100">
                                        <div className="h-2 rounded-full"
                                            style={{ width: `${part}%`, background: 'var(--chart-current)' }} />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            {items.length > 0 && (
                <section>
                    <h2 className="mb-2 text-sm font-semibold">
                        {examen ? 'La note de chaque atelier' : 'Le détail de chaque exercice'}
                    </h2>
                    <ul className="space-y-2">
                        {items.map((it) => (
                            <li key={it.position}>
                                <Link to={`/entrainement/${it.sessionId}`}
                                    className="flex items-center gap-3 rounded-box bg-base-200 p-3 transition-colors hover:bg-base-300">
                                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-base-300 text-sm font-bold">
                                        {it.position}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate font-semibold">
                                            {it.exerciseName ?? modeLabel(it.mode ?? '')}
                                        </span>
                                        {/* Des POINTS quand la séance en compte : « 4 sur 5 »
                                            est la note de l'atelier, « 80 % » ne l'est pas. */}
                                        <span className="block text-xs opacity-60">
                                            {examen && it.pointsMax != null
                                                ? `${it.center} point${it.center > 1 ? 's' : ''} sur ${it.pointsMax}`
                                                : `${num(it.successRate)?.toFixed(0) ?? '—'} % sur ${it.shots} balle${it.shots > 1 ? 's' : ''}`}
                                        </span>
                                    </span>
                                    <ChevronRight size={18} className="shrink-0 opacity-50" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Ce qui reste à faire ailleurs, tout à la fin — après le détail,
                juste avant de ranger le téléphone. Atteindre l'objectif ici ne
                donne pas la carte verte, et rien dans les chiffres ne peut le
                dire : l'examen manquant se passe sur une table, sans un club. */}
            {completionNote && (
                <section className="flex gap-3 rounded-box border p-3"
                    style={{
                        borderColor: 'var(--tuile)',
                        background: 'color-mix(in oklab, var(--tuile) 35%, transparent)',
                    }}>
                    <ClipboardCheck size={18} className="mt-0.5 shrink-0"
                        style={{ color: 'var(--tuile-encre)' }} aria-hidden="true" />
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--tuile-encre)' }}>
                        {completionNote}
                    </p>
                </section>
            )}

            {/* Refaire : c'est ce qu'on veut d'un plan qui a marché, et sans ce
                bouton il faudrait remonter trois écrans pour le retrouver. */}
            {run.workoutId && (
                <button type="button" disabled={busy} onClick={() => void refaire()}
                    className="btn btn-primary btn-block h-14 gap-2 text-lg font-bold tap-target">
                    <RotateCcw size={20} /> Refaire cette séance
                </button>
            )}

            <Link to="/entrainement" className="btn btn-ghost btn-block">
                Retour à l'entraînement
            </Link>
        </div>
    );
}
